import React, { useState } from "react";
import {
  Container,
  Card,
  CardBody,
  Row,
  Col,
  Label,
  Input,
  Form,
  InputGroup,
  InputGroupText,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import showAlert from "../../components/Common/ShowAlert";
import apiClient from "../../axiosInstance";

const ChangePassword = () => {
  const [breadcrumbItems] = useState([
    { title: "Daryeel", link: "/" },
    { title: "Change Password", link: "#" },
  ]);

  const [formData, setFormData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const hasMinLength = formData.newPassword.length >= 6;
  const hasUppercase = /[A-Z]/.test(formData.newPassword);
  const hasNumber = /[0-9]/.test(formData.newPassword);
  const hasSpecialChar = /[^A-Za-z0-9]/.test(formData.newPassword);
  const isPasswordMatch =
    formData.confirmPassword.length > 0 &&
    formData.newPassword === formData.confirmPassword;

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleReset = () => {
    setFormData({
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const authUser = JSON.parse(localStorage.getItem("authUser") || "null");
    const userId = authUser?.user?.id;

    if (!userId) {
      await showAlert({
        title: "Change Password",
        text: "User session is invalid. Please login again.",
        icon: "error",
      });
      return;
    }

    if (!formData.oldPassword || !formData.newPassword || !formData.confirmPassword) {
      await showAlert({
        title: "Change Password",
        text: "All fields are required",
        icon: "warning",
      });
      return;
    }

    if (formData.newPassword.length < 6) {
      await showAlert({
        title: "Change Password",
        text: "New password must be at least 6 characters",
        icon: "warning",
      });
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      await showAlert({
        title: "Change Password",
        text: "New password and confirm password do not match",
        icon: "warning",
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const response = await apiClient.post("/auth/change-password", {
        userId,
        oldPassword: formData.oldPassword,
        newPassword: formData.newPassword,
      });

      await showAlert({
        title: "Change Password",
        text: response?.data?.message || "Password changed successfully",
        icon: "success",
      });

      handleReset();
    } catch (error) {
      await showAlert({
        title: "Change Password",
        text:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Failed to change password",
        icon: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Change Password" breadcrumbItems={breadcrumbItems} />

        <Row className="justify-content-center">
          <Col xl="12" lg="12" md="12">
            <Card className="mb-0">
              <CardBody className="p-4 p-md-5">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <div>
                    <h4 className="card-title mb-1">Change Password</h4>
                    <p className="text-muted mb-0">
                      Enter your current password and set a secure new password.
                    </p>
                  </div>
                  <span className="badge bg-soft-primary text-primary px-3 py-2">
                    Security
                  </span>
                </div>
                <hr className="mt-0 mb-4" />

                <Form onSubmit={handleSubmit}>
                  <div className="bg-light-subtle border rounded p-3 p-md-4 mb-4">
                    <Row className="g-3">
                      <Col md="12">
                        <Label className="form-label fw-semibold" htmlFor="oldPassword">
                          Current Password
                        </Label>
                        <InputGroup>
                          <InputGroupText>
                            <i className="ri-lock-line"></i>
                          </InputGroupText>
                          <Input
                            id="oldPassword"
                            name="oldPassword"
                            type={showCurrentPassword ? "text" : "password"}
                            placeholder="Enter current password"
                            value={formData.oldPassword}
                            onChange={handleInputChange}
                            autoComplete="current-password"
                          />
                          <button
                            type="button"
                            className="btn btn-light border"
                            onClick={() => setShowCurrentPassword((prev) => !prev)}
                          >
                            <i
                              className={
                                showCurrentPassword ? "ri-eye-off-line" : "ri-eye-line"
                              }
                            ></i>
                          </button>
                        </InputGroup>
                      </Col>

                      <Col md="6">
                        <Label className="form-label fw-semibold" htmlFor="newPassword">
                          New Password
                        </Label>
                        <InputGroup>
                          <InputGroupText>
                            <i className="ri-shield-keyhole-line"></i>
                          </InputGroupText>
                          <Input
                            id="newPassword"
                            name="newPassword"
                            type={showNewPassword ? "text" : "password"}
                            placeholder="Enter new password"
                            value={formData.newPassword}
                            onChange={handleInputChange}
                            autoComplete="new-password"
                          />
                          <button
                            type="button"
                            className="btn btn-light border"
                            onClick={() => setShowNewPassword((prev) => !prev)}
                          >
                            <i
                              className={
                                showNewPassword ? "ri-eye-off-line" : "ri-eye-line"
                              }
                            ></i>
                          </button>
                        </InputGroup>
                      </Col>

                      <Col md="6">
                        <Label className="form-label fw-semibold" htmlFor="confirmPassword">
                          Confirm New Password
                        </Label>
                        <InputGroup>
                          <InputGroupText>
                            <i className="ri-shield-check-line"></i>
                          </InputGroupText>
                          <Input
                            id="confirmPassword"
                            name="confirmPassword"
                            type={showConfirmPassword ? "text" : "password"}
                            placeholder="Confirm new password"
                            value={formData.confirmPassword}
                            onChange={handleInputChange}
                            autoComplete="new-password"
                          />
                          <button
                            type="button"
                            className="btn btn-light border"
                            onClick={() => setShowConfirmPassword((prev) => !prev)}
                          >
                            <i
                              className={
                                showConfirmPassword ? "ri-eye-off-line" : "ri-eye-line"
                              }
                            ></i>
                          </button>
                        </InputGroup>
                        {formData.confirmPassword.length > 0 && (
                          <p className={`mt-2 mb-0 ${isPasswordMatch ? "text-success" : "text-danger"}`}>
                            <i
                              className={`me-1 ${
                                isPasswordMatch ? "ri-check-line" : "ri-close-line"
                              }`}
                            ></i>
                            {isPasswordMatch
                              ? "Passwords match"
                              : "Passwords do not match"}
                          </p>
                        )}
                      </Col>
                    </Row>
                  </div>

                  <div className="mb-4 border rounded p-3">
                    <p className="mb-2 fw-semibold">Password requirements</p>
                    <Row>
                      <Col md="6">
                        <ul className="list-unstyled mb-0">
                          <li className={hasMinLength ? "text-success" : "text-muted"}>
                            <i
                              className={`me-1 ${
                                hasMinLength ? "ri-checkbox-circle-line" : "ri-checkbox-blank-circle-line"
                              }`}
                            ></i>
                            Minimum 6 characters
                          </li>
                          <li className={hasUppercase ? "text-success" : "text-muted"}>
                            <i
                              className={`me-1 ${
                                hasUppercase
                                  ? "ri-checkbox-circle-line"
                                  : "ri-checkbox-blank-circle-line"
                              }`}
                            ></i>
                            At least one uppercase letter
                          </li>
                        </ul>
                      </Col>
                      <Col md="6" className="mt-2 mt-md-0">
                        <ul className="list-unstyled mb-0">
                          <li className={hasNumber ? "text-success" : "text-muted"}>
                            <i
                              className={`me-1 ${
                                hasNumber ? "ri-checkbox-circle-line" : "ri-checkbox-blank-circle-line"
                              }`}
                            ></i>
                            At least one number
                          </li>
                          <li className={hasSpecialChar ? "text-success" : "text-muted"}>
                            <i
                              className={`me-1 ${
                                hasSpecialChar
                                  ? "ri-checkbox-circle-line"
                                  : "ri-checkbox-blank-circle-line"
                              }`}
                            ></i>
                            At least one special character
                          </li>
                        </ul>
                      </Col>
                    </Row>
                  </div>

                  <div className="d-flex gap-2 justify-content-end border-top pt-3">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Updating..." : "Update Password"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-light border"
                      onClick={handleReset}
                      disabled={isSubmitting}
                    >
                      Reset
                    </button>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ChangePassword;
